/**
 * Thread document schema for the client SDK.
 *
 * Extends the SDK-agnostic base from `../documents/thread` with
 * `Timestamp`-transforming timestamp fields, and exposes typed refs used by the
 * webapp's Reports triage UI to list conversation threads.
 */
import type {
  CollectionReference,
  DocumentReference,
  Firestore,
} from 'firebase/firestore';
import {z} from 'zod';
import {collection, doc, getFirestore} from 'firebase/firestore';

import {
  ThreadDocumentSchema,
  getThreadDocumentPath,
  getThreadsCollectionPath,
} from '../documents/thread';
import {ClientTimestampSchema, createConverter} from './sdk';

/** Client-SDK thread schema; timestamps become `Timestamp` instances. */
export const ThreadSchema = ThreadDocumentSchema.extend({
  lastMessageAt: ClientTimestampSchema,
  firstMessageAt: ClientTimestampSchema,
  updatedAt: ClientTimestampSchema,
}).loose();

/** Inferred type for {@link ThreadSchema}. */
export type Thread = z.infer<typeof ThreadSchema>;

const threadConverter = createConverter({
  schema: ThreadSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

/**
 * Typed ref to a bucket's threads subcollection. Apply `query`/`orderBy` at the
 * call site.
 *
 * @param args.bucketId - The bucket (course id or the bulk bucket id).
 * @param args.firestore - Firestore instance; defaults to `getFirestore()`
 *   when omitted.
 * @returns Converter-bound collection ref for `reports/{bucketId}/threads`.
 * @throws Never (ref construction only).
 */
export function getThreadsCollectionRef({
  bucketId,
  firestore = getFirestore(),
}: {
  bucketId: string;
  firestore?: Firestore;
}): CollectionReference<Thread> {
  return collection(
    firestore,
    getThreadsCollectionPath({bucketId}),
  ).withConverter(threadConverter);
}

/**
 * Typed ref to a single thread document.
 *
 * @param args.bucketId - The bucket the thread lives in.
 * @param args.threadId - The (sanitized) conversation id.
 * @param args.firestore - Firestore instance; defaults to `getFirestore()`
 *   when omitted.
 * @returns Converter-bound doc ref for `reports/{bucketId}/threads/{threadId}`.
 * @throws Never (ref construction only).
 */
export function getThreadDocRef({
  bucketId,
  threadId,
  firestore = getFirestore(),
}: {
  bucketId: string;
  threadId: string;
  firestore?: Firestore;
}): DocumentReference<Thread> {
  return doc(
    firestore,
    getThreadDocumentPath({bucketId, threadId}),
  ).withConverter(threadConverter);
}
